import PropTypes from 'prop-types';
import React from 'react';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { FormattedMessage } from 'react-intl';

import { accountShape, branchShape } from '@bibliocommons/bc-prop-types';
import { bindAll } from '@bibliocommons/utils-react';
import Checkbox from '@bibliocommons/deprecated-base-checkbox';
import {
  InlineOverlay,
  InlineOverlayHeader,
  InlineOverlayFooter,
  InlineOverlayBody,
  InlineOverlayCloseButton
} from '@bibliocommons/base-inline-overlay';
import { TextButton, PrimaryButton } from '@bibliocommons/deprecated-base-buttons';
import { Checkmark, Info } from '@bibliocommons/deprecated-base-icons';
import { GenericForm } from '@bibliocommons/shared-messages';

import HoldInfoMessages from 'app/components/shared/HoldInfoMessages';
import PickupBranchSelect from 'app/components/shared/PickupBranchSelect';
import BibBrief from 'app/components/shared/BibBrief';
import { MATERIAL_TYPE_PHYSICAL } from 'app/constants/BibConstants';
import MessagingContainer from 'app/components/Messaging/MessagingContainer';

import '../styles/PhysicalHoldsWorkflowView.scss';

class PhysicalHoldsWorkflowView extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      pickupLocation: props.preferredBranches.first() || Immutable.Map(),
      singleClickHoldsEnabled: false,
      singleClickHoldsInfoOpen: false
    };

    bindAll(this);
  }

  handlePlaceHold() {
    this.props.placeHold({
      workflowId: this.props.workflowId,
      messagingId: this.props.messagingId,
      accountId: this.props.accountId,
      metadataId: this.props.bib.get('id'),
      materialType: MATERIAL_TYPE_PHYSICAL,
      enableSingleClickHolds: this.state.singleClickHoldsEnabled,
      materialParams: {
        branchId: this.state.pickupLocation.get('code'),
        expiryDate: null,
        errorMessageLocale: this.props.currentLocale
      },
      reciprocalRank: this.props.reciprocalRank
    });
    this.props.handleClose();
  }

  handleUpdatePickupLocation(pickupLocation) {
    this.setState({
      pickupLocation
    });
  }

  handleUpdateSingleClickHolds(value) {
    this.setState({
      singleClickHoldsEnabled: value
    });
  }

  handleClickInfoButton() {
    this.setState(state => ({
      singleClickHoldsInfoOpen: !state.singleClickHoldsInfoOpen
    }));
  }

  renderSingleClickHoldsInfo() {
    if (this.state.singleClickHoldsInfoOpen) {
      return (
        <div className="text-left single-click-holds-info">
          <FormattedMessage id="single_click_holds_enable_desc" tagName="p" />
        </div>
      );
    }

    return null;
  }

  render() {
    const { handleClose, bib, branches, preferredBranches, currentAccount, holdInfoMessages, isLoading } = this.props;

    return (
      <InlineOverlay handleClose={handleClose} testId={`physical-holds-overlay-${bib.get('id')}`}>
        <InlineOverlayHeader>
          <FormattedMessage id="section_title_request_hold" />
        </InlineOverlayHeader>
        <InlineOverlayBody>
          <MessagingContainer id={bib.get('id')}>
            {messageOptions => <GenericForm {...messageOptions} />}
          </MessagingContainer>
          <BibBrief bib={bib} titleTag="h3" renderTitleAsLink={false} renderAuthorsAsLink={false} />
          <HoldInfoMessages messageIds={holdInfoMessages} />
        </InlineOverlayBody>
        <InlineOverlayFooter>
          <form className="form-horizontal">
            <div className="branch-select-column">
              <div className="clearfix">
                {branches.size > 0 && (
                  <PickupBranchSelect
                    fieldErrors={this.props.fieldErrors}
                    branches={branches}
                    preferredBranches={preferredBranches}
                    currentAccount={currentAccount}
                    label={<FormattedMessage id="pick_up_at" />}
                    handleSelect={this.handleUpdatePickupLocation}
                    selectedBranch={this.state.pickupLocation.get('code')}
                  />
                )}
              </div>

              <div className="text-left enable-single-click-holds-wrap">
                <Checkbox
                  label={<FormattedMessage id="single_click_holds_enable_title" />}
                  id={`enable-single-click-holds-${bib.get('id')}`}
                  checked={this.state.singleClickHoldsEnabled}
                  handleChange={this.handleUpdateSingleClickHolds}
                />
                <TextButton className="help-info" handleClick={this.handleClickInfoButton}>
                  <Info />
                  <FormattedMessage id="label_info" />
                </TextButton>
              </div>
            </div>
            <div className="holds-action-column">
              <PrimaryButton
                className="pull-right"
                isLoading={isLoading}
                handleClick={this.handlePlaceHold}
                data-test-id={`submit-place-hold-${bib.get('id')}`}
              >
                <Checkmark color="#FFF" />
                &nbsp;
                <FormattedMessage id="button_confirm_hold" />
              </PrimaryButton>
              <InlineOverlayCloseButton className="btn btn-link pull-right">
                <FormattedMessage id="button_nevermind_close" />
              </InlineOverlayCloseButton>
            </div>
            {this.renderSingleClickHoldsInfo()}
          </form>
        </InlineOverlayFooter>
      </InlineOverlay>
    );
  }
}

PhysicalHoldsWorkflowView.displayName = 'PhysicalHoldsWorkflowView';
PhysicalHoldsWorkflowView.propTypes = {
  handleClose: PropTypes.func.isRequired,
  fieldErrors: PropTypes.objectOf(
    PropTypes.shape({
      classification: PropTypes.string,
      message: PropTypes.string
    })
  ),
  bib: ImmutablePropTypes.map,
  branches: ImmutablePropTypes.listOf(branchShape).isRequired,
  preferredBranches: ImmutablePropTypes.listOf(branchShape).isRequired,
  currentAccount: accountShape.isRequired,
  placeHold: PropTypes.func.isRequired,
  accountId: PropTypes.number.isRequired,
  currentLocale: PropTypes.string.isRequired,
  holdInfoMessages: ImmutablePropTypes.listOf(PropTypes.number),
  isLoading: PropTypes.bool,
  workflowId: PropTypes.string,
  messagingId: PropTypes.string,
  reciprocalRank: PropTypes.number
};

export default PhysicalHoldsWorkflowView;
